import { ChevronDown, ChevronUp, Eye, GripVertical, RotateCcw, Save, X } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  useUpdatePdfTemplateMutation,
  useResetPdfTemplateMutation,
  useGetPdfTemplateSectionsQuery,
} from '../../services/pdfTemplateApi';
import type {
  PdfTemplate,
  PdfTemplateHeader,
  PdfTemplateFooter,
  PdfTemplateColors,
  PdfTemplateTypography,
  PdfTemplatePageSettings,
  PdfTemplateSection,
  PdfTemplateWatermark,
  UpdatePdfTemplateRequest,
} from '../../types/pdfTemplate';
import PdfHeaderSettings from './PdfHeaderSettings';
import PdfFooterSettings from './PdfFooterSettings';
import PdfColorSettings from './PdfColorSettings';
import PdfSectionManager from './PdfSectionManager';
import PdfPageSettings from './PdfPageSettings';
import PdfLabelOverrides from './PdfLabelOverrides';

interface PdfTemplateEditorProps {
  template: PdfTemplate;
  isOpen: boolean;
  onClose: () => void;
}

type TabId = 'page' | 'header' | 'footer' | 'colors' | 'sections' | 'labels' | 'watermark';

const tabs: Array<{ id: TabId; label: string }> = [
  { id: 'page', label: 'Page' },
  { id: 'header', label: 'Header' },
  { id: 'footer', label: 'Footer' },
  { id: 'colors', label: 'Colors' },
  { id: 'sections', label: 'Sections' },
  { id: 'labels', label: 'Labels' },
  { id: 'watermark', label: 'Watermark' },
];

const PdfTemplateEditor: React.FC<PdfTemplateEditorProps> = ({ template, isOpen, onClose }) => {
  const [updateTemplate, { isLoading: isUpdating }] = useUpdatePdfTemplateMutation();
  const [resetTemplate, { isLoading: isResetting }] = useResetPdfTemplateMutation();
  const { data: sectionsResponse } = useGetPdfTemplateSectionsQuery(template.key);

  const [activeTab, setActiveTab] = useState<TabId>('header');
  const [isDirty, setIsDirty] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Form state
  const [pageSettings, setPageSettings] = useState<PdfTemplatePageSettings>(template.pageSettings);
  const [header, setHeader] = useState<PdfTemplateHeader>(template.header);
  const [footer, setFooter] = useState<PdfTemplateFooter>(template.footer);
  const [colors, setColors] = useState<PdfTemplateColors>(template.colors);
  const [typography, setTypography] = useState<PdfTemplateTypography>(template.typography);
  const [sections, setSections] = useState<PdfTemplateSection[]>(template.sections);
  const [customLabels, setCustomLabels] = useState<Record<string, string>>(
    template.customLabels || {}
  );
  const [watermark, setWatermark] = useState<PdfTemplateWatermark>(
    template.watermark || { enabled: false, text: 'DRAFT', opacity: 0.1, rotation: -45 }
  );

  // Reset form when template changes
  useEffect(() => {
    setPageSettings(template.pageSettings);
    setHeader(template.header);
    setFooter(template.footer);
    setColors(template.colors);
    setTypography(template.typography);
    setSections(template.sections);
    setCustomLabels(template.customLabels || {});
    setWatermark(
      template.watermark || { enabled: false, text: 'DRAFT', opacity: 0.1, rotation: -45 }
    );
    setIsDirty(false);
    setMessage(null);
  }, [template]);

  const markDirty = useCallback(() => setIsDirty(true), []);

  const handleSave = async () => {
    try {
      const data: UpdatePdfTemplateRequest = {
        pageSettings,
        header,
        footer,
        colors,
        typography,
        sections,
        customLabels,
        watermark,
      };

      await updateTemplate({ id: template._id, data }).unwrap();
      setIsDirty(false);
      setMessage({ type: 'success', text: 'Template saved successfully' });
      setTimeout(() => setMessage(null), 3000);
    } catch {
      setMessage({ type: 'error', text: 'Failed to save template' });
    }
  };

  const handleReset = async () => {
    if (!confirm('Reset this template to the system default? Your customizations will be lost.')) {
      return;
    }

    try {
      const result = await resetTemplate(template._id).unwrap();
      if (result.data) {
        setPageSettings(result.data.pageSettings);
        setHeader(result.data.header);
        setFooter(result.data.footer);
        setColors(result.data.colors);
        setTypography(result.data.typography);
        setSections(result.data.sections);
        setCustomLabels(result.data.customLabels || {});
        setWatermark(
          result.data.watermark || { enabled: false, text: 'DRAFT', opacity: 0.1, rotation: -45 }
        );
        setIsDirty(false);
        setMessage({ type: 'success', text: 'Template reset to default' });
        setTimeout(() => setMessage(null), 3000);
      }
    } catch {
      setMessage({ type: 'error', text: 'Failed to reset template' });
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]" onClick={onClose} />

      {/* Drawer - 90% width for large form */}
      <div className="fixed inset-6 z-[100] animate-in slide-in-from-right">
        <div className="bg-white rounded-2xl shadow-2xl h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{template.name}</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Module: {template.moduleType} | Version: {template.version}
                {template.isSystem && !template.organizationId && ' | System Default'}
              </p>
            </div>
            <div className="flex items-center space-x-3">
              {message && (
                <span
                  className={`text-sm px-3 py-1 rounded-full ${
                    message.type === 'success'
                      ? 'bg-green-50 text-green-700'
                      : 'bg-red-50 text-red-700'
                  }`}
                >
                  {message.text}
                </span>
              )}
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="px-8 border-b bg-gray-50">
            <div className="flex space-x-1 overflow-x-auto">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors relative ${
                    activeTab === tab.id ? 'text-brand-600' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.label}
                  {activeTab === tab.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-600" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-8 py-6">
            <div className="max-w-3xl">
              {activeTab === 'page' && (
                <PdfPageSettings
                  settings={pageSettings}
                  typography={typography}
                  onChange={settings => {
                    setPageSettings(settings);
                    markDirty();
                  }}
                  onTypographyChange={typo => {
                    setTypography(typo);
                    markDirty();
                  }}
                />
              )}

              {activeTab === 'header' && (
                <PdfHeaderSettings
                  header={header}
                  onChange={h => {
                    setHeader(h);
                    markDirty();
                  }}
                />
              )}

              {activeTab === 'footer' && (
                <PdfFooterSettings
                  footer={footer}
                  onChange={f => {
                    setFooter(f);
                    markDirty();
                  }}
                />
              )}

              {activeTab === 'colors' && (
                <PdfColorSettings
                  colors={colors}
                  onChange={c => {
                    setColors(c);
                    markDirty();
                  }}
                />
              )}

              {activeTab === 'sections' && (
                <PdfSectionManager
                  sections={sections}
                  availableSections={sectionsResponse?.data || []}
                  onChange={s => {
                    setSections(s);
                    markDirty();
                  }}
                />
              )}

              {activeTab === 'labels' && (
                <PdfLabelOverrides
                  labels={customLabels}
                  sections={sections}
                  onChange={l => {
                    setCustomLabels(l);
                    markDirty();
                  }}
                />
              )}

              {activeTab === 'watermark' && (
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-900">Watermark Settings</h3>
                  <div className="flex items-center space-x-3">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={watermark.enabled}
                        onChange={e => {
                          setWatermark({ ...watermark, enabled: e.target.checked });
                          markDirty();
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:bg-brand-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all" />
                    </label>
                    <span className="text-sm text-gray-700">Enable watermark</span>
                  </div>

                  {watermark.enabled && (
                    <div className="space-y-3 mt-4">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Watermark Text</label>
                        <input
                          type="text"
                          value={watermark.text}
                          onChange={e => {
                            setWatermark({ ...watermark, text: e.target.value });
                            markDirty();
                          }}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">
                            Opacity ({Math.round(watermark.opacity * 100)}%)
                          </label>
                          <input
                            type="range"
                            min="0.01"
                            max="0.5"
                            step="0.01"
                            value={watermark.opacity}
                            onChange={e => {
                              setWatermark({ ...watermark, opacity: parseFloat(e.target.value) });
                              markDirty();
                            }}
                            className="w-full"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">
                            Rotation ({watermark.rotation}&deg;)
                          </label>
                          <input
                            type="range"
                            min="-90"
                            max="90"
                            step="5"
                            value={watermark.rotation}
                            onChange={e => {
                              setWatermark({ ...watermark, rotation: parseInt(e.target.value) });
                              markDirty();
                            }}
                            className="w-full"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t px-8 py-4 bg-gray-50 rounded-b-2xl flex items-center justify-between">
            <button
              onClick={handleReset}
              disabled={isResetting}
              className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
            >
              <RotateCcw className="h-4 w-4" />
              <span>Reset to Default</span>
            </button>

            <div className="flex items-center space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!isDirty || isUpdating}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 disabled:bg-brand-200 disabled:cursor-not-allowed"
              >
                <Save className="h-4 w-4" />
                <span>{isUpdating ? 'Saving...' : 'Save Changes'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default PdfTemplateEditor;
